import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';

import CommonCarousel from '../../components/common-carousel/CommonCarousel';
import { setSelectedMediaIndex } from '../../redux/ai-studio/aiStudioReducer';
import EmptyDisclaimer from './empty-disclaimer/EmptyDisclaimer';
import CollapsedButton, {
  COLLAPSED_BUTTON_SIZES,
  COLLAPSED_BUTTON_TYPES,
} from '../../components/collapsed-button/CollapsedButton';
import { clearAllAiStudio } from '../../redux/ai-studio/aiStudioActions';
import useCommonModal from '../../hooks/useCommonModal';
import ConfirmationModal from '../../components/confirmation-modal/ConfirmationModal';
import { clampMediaIndex } from '../../activ8/utils/mediaIndex';

import scss from './MediaSelector.module.scss';

function MediaSelector({ className, style, AdditionalContent, Slide, isVideoEnabled }) {
  const { t } = useTranslation('studio');
  const dispatch = useDispatch();

  const selectedMediaIndex = useSelector((state) => state.aiStudio.selectedMediaIndex);
  const bucket = useSelector((state) => state.aiStudio.bucket);
  const mediaLength = bucket?.media?.length ?? 0;
  const safeIndex = clampMediaIndex(selectedMediaIndex, mediaLength);

  const {
    isOpen: isClearStudioModalOpen,
    handleOpenModal: handleOpenClearStudioModal,
    handleCloseModal: handleCloseClearStudioModal,
  } = useCommonModal();

  const handleAfterChange = (current) => {
    dispatch(setSelectedMediaIndex(current));
  };

  const handleClearAiStudio = () => {
    dispatch(clearAllAiStudio());
  };

  useEffect(() => {
    if (!mediaLength || selectedMediaIndex === safeIndex) {
      return;
    }

    dispatch(setSelectedMediaIndex(safeIndex));
  }, [mediaLength, safeIndex, selectedMediaIndex, dispatch]);

  if (!bucket) {
    return <EmptyDisclaimer style={style} isVideoEnabled={isVideoEnabled} />;
  }

  return (
    <div className={clsx(className, scss.wrapper)} style={style}>
      <div className={scss.infoWrapper}>
        <div className={scss.infoHeader}>
          <div className={scss.title}>{t('mediaToAdjust')}:</div>
          <CollapsedButton
            type={COLLAPSED_BUTTON_TYPES.PRIMARY}
            size={COLLAPSED_BUTTON_SIZES.MEDIUM}
            onClick={handleOpenClearStudioModal}
            icon={CleaningServicesOutlinedIcon}
            disableOpenOnHover
            label={t('clearStudio')}
            tooltip={t('clearStudio')}
          />
        </div>
        {AdditionalContent ? (
          <div className={scss.additionContentWrapper}>
            <AdditionalContent slide={bucket.media[safeIndex]} />
          </div>
        ) : null}
      </div>

      <div className={scss.sliderWrapper}>
        <CommonCarousel
          key={`${bucket.bucketId}-${mediaLength}`}
          isLazyLoad
          contrastedArrows
          draggable
          swipe
          initialSlide={safeIndex}
          extraSettings={{ afterChange: handleAfterChange }}>
          {bucket.media.map((slide) => (
            <Slide key={slide.id} slide={slide} />
          ))}
        </CommonCarousel>
      </div>

      {AdditionalContent ? (
        <div className={scss.mobileAdditionContentWrapper}>
          <AdditionalContent slide={bucket.media[safeIndex]} />
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={isClearStudioModalOpen}
        onClose={handleCloseClearStudioModal}
        onCancel={handleCloseClearStudioModal}
        onConfirm={handleClearAiStudio}
        label={t('areYouSureYouWantToClearStudio')}
        subLabel={t('allMediaThatWasCreatedInPreviousStudioSessionWillBeCleared')}
      />
    </div>
  );
}

export default MediaSelector;
